import React, { useState, useEffect } from "react";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./HistoryLogs.css";
import usePageTitle from "../usePageTitle";
import HistoryLogsHeaderComponent from "./HistoryLogsHeaderComponent";
import HistoryLogsDatatableComponent from "./HistoryLogsDatatableComponent";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { fetchHistoryLogs } from "./api";

const HistoryLogs = () => {
  usePageTitle("Activity Logs - SkeyeForm™");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(24);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchHistoryLogs();
        if (Array.isArray(response.data)) {
          setTotalRecords(response.data.length);
          setData(response.data);
          setFilteredData(response.data.slice(first, first + rows));
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error fetching History Logs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [first, rows]);

  useEffect(() => {
    if (Array.isArray(data)) {
      let filtered = data.filter((log) => {
        const searchValue = globalFilterValue.toLowerCase();

        // Safely check each field with null checks and convert to string
        const action = (log.action || "").toLowerCase();
        const username = (log.username || "").toLowerCase();
        const formSchemaId = (log.formSchemaId || "").toString().toLowerCase();
        const filledFormId = (log.filledFormId || "").toString().toLowerCase();
        const ipAddress = (log.ipAddress || "").toLowerCase();
        const timestamp = log.timestamp
          ? new Date(log.timestamp).toLocaleString().toLowerCase()
          : "";

        // Check if any field matches the search value
        return (
          action.includes(searchValue) ||
          username.includes(searchValue) ||
          formSchemaId.includes(searchValue) ||
          filledFormId.includes(searchValue) ||
          ipAddress.includes(searchValue) ||
          timestamp.includes(searchValue)
        );
      });

      if (sortField) {
        filtered = filtered.sort((a, b) => {
          const valueA = a[sortField] || ""; // Provide default empty string
          const valueB = b[sortField] || ""; // Provide default empty string
          let result = 0;

          if (valueA < valueB) result = -1;
          else if (valueA > valueB) result = 1;

          return sortOrder * result;
        });
      }

      setFilteredData(filtered.slice(first, first + rows));
      setTotalRecords(filtered.length);
    }
  }, [globalFilterValue, first, rows, data, sortField, sortOrder]);

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const header = (
    <HistoryLogsHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <div className="p-col title">
        <h1>Activity Logs</h1>
        <div className="title-bar"></div>
      </div>
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <HistoryLogsDatatableComponent
          data={filteredData}
          filters={filters}
          setFilters={setFilters}
          header={header}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
          loading={loading}
        />
      )}
    </div>
  );
};

export default HistoryLogs;
